import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { proyectosApi, ordenesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Search, FolderKanban, Pencil, Trash2, Printer, Eye } from 'lucide-react';

export const ProyectosList = () => {
  const [proyectos, setProyectos] = useState([]);
  const [allProyectos, setAllProyectos] = useState([]);
  const [ordenesTrabajo, setOrdenesTrabajo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('ACTIVO'); // Por defecto Activos
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoFilter, allProyectos, ordenesTrabajo]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [proyRes, otsRes] = await Promise.all([
        proyectosApi.getAll({}),
        ordenesApi.getAll({})
      ]);
      
      // Ordenar proyectos por cliente
      const sortedProyectos = proyRes.data.sort((a, b) => {
        const clienteA = (a.cliente_nombre || '').toLowerCase();
        const clienteB = (b.cliente_nombre || '').toLowerCase();
        if (clienteA !== clienteB) return clienteA.localeCompare(clienteB);
        return (a.codigo || '').localeCompare(b.codigo || '');
      });
      
      setAllProyectos(sortedProyectos);
      setOrdenesTrabajo(otsRes.data);
    } catch (error) {

      toast.error('Error al cargar los proyectos');
    } finally {
      setLoading(false);
    }
  };

  // Determinar si un proyecto está Activo o Cerrado
  // Activo: tiene al menos una OT con estfinan = true/SI
  // Cerrado: todas sus OTs tienen estfinan = false/NO
  const getEstadoProyecto = (proyectoId) => {
    const otsDelProyecto = ordenesTrabajo.filter(ot => ot.proyecto_id === proyectoId);
    
    if (otsDelProyecto.length === 0) {
      return 'CERRADO'; // Sin OTs = cerrado
    }
    
    // Verificar si tiene al menos una OT activa (estfinan = true o SI, o estado EN EJECUCION)
    const tieneOTActiva = otsDelProyecto.some(ot => {
      // OT en ejecución siempre está activa
      if (ot.estado === 'EN EJECUCION') return true;
      // Verificar estfinan
      return ot.estfinan === true || ot.estfinan === 'SI';
    });
    
    return tieneOTActiva ? 'ACTIVO' : 'CERRADO';
  };

  // Contar OTs activas de un proyecto
  const countOTsActivas = (proyectoId) => {
    const otsDelProyecto = ordenesTrabajo.filter(ot => ot.proyecto_id === proyectoId);
    return otsDelProyecto.filter(ot => {
      if (ot.estado === 'EN EJECUCION') return true;
      return ot.estfinan === true || ot.estfinan === 'SI';
    }).length;
  };

  const applyFilters = () => {
    let data = allProyectos.map(p => ({
      ...p,
      estadoCalculado: getEstadoProyecto(p.id),
      otsActivas: countOTsActivas(p.id),
      totalOTs: ordenesTrabajo.filter(ot => ot.proyecto_id === p.id).length
    }));
    
    // Filtrar por estado
    if (estadoFilter === 'ACTIVO') {
      data = data.filter(p => p.estadoCalculado === 'ACTIVO');
    } else if (estadoFilter === 'CERRADO') {
      data = data.filter(p => p.estadoCalculado === 'CERRADO');
    }
    // Si es '' (Todos), no filtrar
    
    setProyectos(data);
  };

  const handleDelete = async (id) => {
    try {
      await proyectosApi.delete(id);
      toast.success('Proyecto eliminado');
      loadData();
    } catch (_error) {
      toast.error('Error al eliminar el proyecto');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredProyectos = proyectos.filter((p) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.codigo?.toLowerCase().includes(search) ||
      p.descripcion?.toLowerCase().includes(search) ||
      p.cliente_nombre?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL');
  };

  // Contadores
  const countActivos = allProyectos.filter(p => getEstadoProyecto(p.id) === 'ACTIVO').length;
  const countCerrados = allProyectos.filter(p => getEstadoProyecto(p.id) === 'CERRADO').length;

  if (loading) {
    return (<div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="proyectos-list">
      {/* Print Header */}
      <div className="hidden print:block print:mb-4">
        <div className="text-center border-b-2 border-black pb-2 mb-2">
          <h1 className="text-xl font-bold">CHIRINO STEEL</h1>
          <h2 className="text-lg font-semibold">Proyectos {estadoFilter === 'ACTIVO' ? 'Activos' : estadoFilter === 'CERRADO' ? 'Cerrados' : ''}</h2>
          <p className="text-sm text-gray-600">Fecha: {new Date().toLocaleDateString('es-CL')} | Total: {filteredProyectos.length}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Proyectos</h1>
          <p className="text-muted-foreground">
            {estadoFilter === 'ACTIVO' 
              ? `${proyectos.length} proyectos activos (con OTs activas)`
              : estadoFilter === 'CERRADO'
              ? `${proyectos.length} proyectos cerrados`
              : `${proyectos.length} proyectos (${countActivos} activos, ${countCerrados} cerrados)`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild data-testid="new-proyecto-btn">
            <Link to="/proyectos/nuevo"><Plus className="w-4 h-4 mr-2" />Nuevo Proyecto</Link>
          </Button>
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />Imprimir
          </Button>
        </div>
      </div>

      {deleteConfirm && (
        <Card className="border-2 border-destructive/50 bg-destructive/5 print:hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="font-medium">¿Eliminar proyecto "{deleteConfirm.codigo}"?</p><p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteConfirm.id)}>Eliminar</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por código, descripción, cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
            </div>
            <select 
              value={estadoFilter} 
              onChange={(e) => setEstadoFilter(e.target.value)} 
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm w-full sm:w-[200px]" 
              data-testid="filter-estado"
            >
              <option value="ACTIVO">Activos ({countActivos})</option>
              <option value="CERRADO">Cerrados ({countCerrados})</option>
              <option value="">Todos ({allProyectos.length})</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {filteredProyectos.length === 0 ? (
            <div className="text-center py-12"><FolderKanban className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" /><p className="text-muted-foreground">No se encontraron proyectos</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-muted/50">
                    <TableHead className="py-2">Código</TableHead>
                    <TableHead className="py-2">Cliente</TableHead>
                    <TableHead className="py-2">Descripción</TableHead>
                    <TableHead className="py-2 text-center">Estado</TableHead>
                    <TableHead className="py-2 text-center">OTs</TableHead>
                    <TableHead className="py-2 hidden md:table-cell">F. Inicio</TableHead>
                    <TableHead className="py-2 w-24 print:hidden">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProyectos.map((proyecto) => (
                    <TableRow 
                      key={proyecto.id} 
                      data-testid={`proyecto-row-${proyecto.codigo}`} 
                      className={`text-sm ${proyecto.estadoCalculado === 'CERRADO' ? 'bg-gray-50' : ''}`}
                    >
                      <TableCell className="py-1.5 font-mono font-medium">{proyecto.codigo}</TableCell>
                      <TableCell className="py-1.5 max-w-[120px] truncate">{proyecto.cliente_nombre}</TableCell>
                      <TableCell className="py-1.5 max-w-[200px] truncate">{proyecto.descripcion}</TableCell>
                      <TableCell className="py-1.5 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          proyecto.estadoCalculado === 'ACTIVO' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {proyecto.estadoCalculado}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5 text-center text-xs">
                        <span className="text-green-600 font-medium">{proyecto.otsActivas}</span>
                        <span className="text-gray-400">/{proyecto.totalOTs}</span>
                      </TableCell>
                      <TableCell className="py-1.5 hidden md:table-cell text-xs">{formatDate(proyecto.fecha_inicio)}</TableCell>
                      <TableCell className="py-1.5 print:hidden">
                        <div className="flex gap-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/reportes/informe-proyecto?proyecto=${proyecto.id}`)} title="Ver informe">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/proyectos/${proyecto.id}/editar`)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteConfirm(proyecto)}>
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leyenda */}
      <div className="text-xs text-muted-foreground print:hidden">
        <p><span className="bg-green-100 text-green-700 px-1 rounded">ACTIVO</span> Proyecto con OTs activas en circulante | <span className="bg-gray-200 text-gray-600 px-1 rounded">CERRADO</span> Todas las OTs cerradas</p>
        <p className="mt-1">Columna OTs: <span className="text-green-600">activas</span>/<span className="text-gray-400">total</span></p>
      </div>
    </div>
  );
};
